import classNames from 'classnames';
import { FC, ReactElement, useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import { getProductErrorMessage } from '../../../../../helpers/checkout';
import { IBranch } from 'src/types/branches';
import { IMenuProduct } from 'src/types/products';
import { formatPriceBranch } from 'src/utils/formatter';
import Image from 'src/view/components/Image/Image';
import { productUtils } from '~/utils/productUtils';
import { useCheckoutStoreContext } from '~/view/screens/CheckoutScreen/context';
import { PRODUCT_TYPE } from '~/types/product';
import { localeStore } from '~/mobx/localesStore';
import Label from '~/view/components/Controls/Label/Label';
import { COLORS } from '~/constants/styles';
import './style.scss';

interface IProps {
  index: number;
  product?: IMenuProduct;
  branch?: IBranch;
  onSelect?: (product: IMenuProduct) => void;
}

const MenuProduct: FC<IProps> = (props: IProps): ReactElement => {
  const { store: checkoutStore } = useCheckoutStoreContext();

  const error: string = getProductErrorMessage(props.product, checkoutStore.supportedProductOrderType);
  const units: string[] = productUtils.getProductUnits(props.product);

  const onSelect = () => {
    if (!props.product || !props.onSelect) return;
    props.onSelect(props.product);
  };

  const className = useMemo((): string => {
    return classNames('animate__faster animate__animated animate__fadeInRight menu-product', {
      'menu-product_transparent': error,
    });
  }, [error]);

  const { product, index, branch } = props;

  return (
    <div key={product?.id || index} style={{ animationDelay: `${index * 30}ms` }} onMouseDown={onSelect} className={className}>
      {product?.type === PRODUCT_TYPE.MEAL_COMBO ? (
        <div className="menu-product__label">
          <Label text={localeStore.t('combo')} color={COLORS.WHITE} />
        </div>
      ) : null}

      <Image wrapperClassName="menu-product__image" src={product?.image} />

      {Boolean(error) && <span className="color-red family-medium text-size-12 letter-spacing-12 text-elipsis mb-1">{error}</span>}

      <span className={`family-semibold text-elipsis text-size${Boolean(error) ? '-14' : '-16'}`}>{product?.name || <Skeleton width={100} />}</span>

      {!error ? (
        <div className="d-flex flex-row mt-5 align-items-center justify-content-between">
          <span className="text-size-12 family-medium">
            {product ? formatPriceBranch(productUtils.calculateMenuProductPrice(product), branch) : <Skeleton width={80} />}
          </span>
          <span className="text-size-10 color-gray2">{units.map((unit) => (unit ? <span className="ml-1">{unit}</span> : null))}</span>
        </div>
      ) : null}
    </div>
  );
};

export default MenuProduct;
